import {AttachmentIcon} from '@chakra-ui/icons';
import {Box, Flex, HStack, Image, Link, Skeleton, Text} from '@chakra-ui/react';
import {useHalState} from '@synako/haligator';
import {State} from '@synako/halitrek';
import {MessageDesc} from 'api-types/ffconnect-hal';
import {format} from 'date-fns';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {withClientApp} from '@/utils/appMode';

/**
 * Bulle de message
 *
 * @param isClientView - Si true, on est côté client (les messages contact_client sont à droite)
 *                       Si false/undefined, on est côté back-office (les messages de l'utilisateur FF courant sont à droite)
 */
export const ConversationsMessageBubble: React.FC<{
  messageState: State<MessageDesc>;
  currentCodeUtilisateur?: string;
  isClientView?: boolean;
  onImageLoad?: () => void; // Callback lorsqu'une image est chargée
}> = ({
  messageState,
  currentCodeUtilisateur,
  isClientView = false,
  onImageLoad,
}) => {
  const {t} = useTranslation();
  const message = messageState.data;

  // Pièces jointes
  const piecesJointes = message.pieces_jointes;
  const pjResources = messageState.hasManyLinks('pieces_jointes')
    ? messageState.followAllOrThrow('pieces_jointes')
    : [];

  // Récupérer le code_utilisateur de l'expéditeur via le lien (uniquement pour les utilisateurs FF)
  // Pour les contact_client, le lien n'existe pas donc on ne suit pas le lien
  const isUtilisateurFF = message.expediteur_type === 'utilisateur_ff';
  const expediteurState = useHalState(
    messageState.follow('expediteur_utilisateur_ff'),
    {disable: !isUtilisateurFF},
  );
  const messageCodeUtilisateur = expediteurState.data?.code_utilisateur;

  // Déterminer si le message est de l'utilisateur courant
  // - Côté client : les messages contact_client sont à droite
  // - Côté back-office : les messages de l'utilisateur FF courant sont à droite
  const isCurrentUser = isClientView
    ? message.expediteur_type === 'contact_client'
    : isUtilisateurFF &&
      currentCodeUtilisateur &&
      messageCodeUtilisateur === currentCodeUtilisateur;

  // Couleurs des bulles : blue.main pour l'interface client, gray.dark pour le back-office
  const bgUtilisateurCourant = isClientView ? 'blue.main' : 'gray.dark';
  const bgAutreUtilisateur = 'white';

  // State pour le chargement des images
  const [imagesLoaded, setImagesLoaded] = useState<Set<number>>(new Set());

  return (
    <Flex
      justify={isCurrentUser ? 'flex-end' : 'flex-start'}
      mb={3}
    >
      <Box maxW={{base: '95%', md: '70%'}}>
        {' '}
        {/* Limiter la largeur des bulles */}
        {/* Bulle du message */}
        <Box
          bg={isCurrentUser ? bgUtilisateurCourant : bgAutreUtilisateur}
          color={isCurrentUser ? 'white' : 'gray.dark'}
          px={4}
          py={3}
          borderRadius='lg'
        >
          <Text
            fontSize='sm'
            whiteSpace='pre-wrap'
          >
            {message.contenu}
          </Text>
          {/* Pièces jointes */}
          {piecesJointes.map((pj, index) => {
            // Le navigateur charge src et href lui-même, sans le header x-client-app de halitrek
            const pjUri = pjResources[index]?.uri;
            const pjUrl = pjUri ? withClientApp(pjUri) : '#';
            const isImage = pj.type.startsWith('image/');
            return (
              <Link
                key={pj.id}
                href={pjUrl}
                isExternal
                mt={2}
                display='block'
              >
                {isImage ? (
                  // Vignette pour les images avec skeleton pendant le chargement
                  <Box position='relative'>
                    {!imagesLoaded.has(index) && (
                      <Skeleton
                        height='150px'
                        width='150px'
                        borderRadius='8px'
                      />
                    )}
                    <Image
                      src={pjUrl}
                      alt={pj.nom}
                      maxH='150px'
                      borderRadius='8px'
                      objectFit='cover'
                      display={imagesLoaded.has(index) ? 'block' : 'none'}
                      onLoad={() => {
                        setImagesLoaded((prev) => new Set([...prev, index]));
                        onImageLoad?.();
                      }}
                    />
                  </Box>
                ) : (
                  // Affichage classique pour les documents
                  <HStack
                    spacing={2}
                    bg={
                      isCurrentUser
                        ? isClientView
                          ? 'blue.600'
                          : 'gray.800'
                        : 'gray.light'
                    }
                    p={2}
                    borderRadius='md'
                    _hover={{
                      bg: isCurrentUser
                        ? isClientView
                          ? 'blue.700'
                          : 'gray.dark'
                        : 'gray.bg',
                    }}
                  >
                    <AttachmentIcon boxSize={4} />
                    <Text
                      fontSize='xs'
                      noOfLines={1}
                      flex={1}
                    >
                      {pj.nom}
                    </Text>
                    <Text
                      fontSize='xs'
                      opacity={0.7}
                    >
                      {t(
                        isClientView
                          ? 'client.conversations.taille_fichier_ko'
                          : 'back_office.conversations.taille_fichier_ko',
                        {taille: (pj.taille / 1024).toFixed(1)},
                      )}
                    </Text>
                  </HStack>
                )}
              </Link>
            );
          })}
        </Box>
        {/* Nom de l'expéditeur et heure */}
        <Text
          fontSize='xs'
          color='gray.dark'
          fontWeight='700'
          mt={1}
          textAlign={isCurrentUser ? 'right' : 'left'}
        >
          {message.expediteur_nom_complet} -{' '}
          {format(new Date(message.envoye_le), 'HH:mm')}
        </Text>
      </Box>
    </Flex>
  );
};
